/* eslint-disable sort-imports */
/**
 * View 2 — Skill-gap board.
 *
 * Matrix of work centre × job role × 4-hour block on the left, cover rail on
 * the right. Click a short cell, pick how to cover it, and the cell turns to a
 * drafted tick. Nothing leaves the browser until Publish.
 */
import React from "react";

import { COVER_LABEL, CoverMethod } from "../types";
import { GapCell, gapKey, GapRow, SHIFT_BLOCKS, SHIFT_LABEL, ShiftNumber } from "../utils/gap-derive";
import { eyebrowSx, lbb } from "../utils/tokens";

interface GapBoardProps {
	rows: GapRow[]
	shift: ShiftNumber
	drafts: Record<string, string>
	selected: string | null
	onSelect: (key: string | null) => void
	onDraft: (key: string, method: CoverMethod) => void
	shortBlocks: number
	uncoveredHours: number
	/** Work centres with a spare qualified head, for the "pull from" option. */
	pullSource: (row: GapRow, cell: GapCell) => { workcenter: string; detail: string } | null
}

const CELL_BASE: React.CSSProperties = {
	flex: 1,
	height: 46,
	borderRadius: 2,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	fontFamily: lbb.fontMono,
	fontWeight: 700,
	fontSize: 17,
	border: `1px solid ${lbb.hairline}`,
	transition: `all 160ms ${lbb.ease}`,
	background: lbb.paper,
};

function GapBoard({
	rows,
	shift,
	drafts,
	selected,
	onSelect,
	onDraft,
	shortBlocks,
	uncoveredHours,
	pullSource,
}: GapBoardProps) {
	const blocks = SHIFT_BLOCKS[shift];
	const selectedRow = selected
		? rows.find((row) =>
				row.cells.some((cell) => gapKey(row.workcenter, row.jobRole, cell.blockIndex) === selected)
			) || null
		: null;
	const selectedCell =
		selectedRow?.cells.find(
			(cell) => gapKey(selectedRow.workcenter, selectedRow.jobRole, cell.blockIndex) === selected
		) || null;

	return (
		<div style={{ display: "grid", gridTemplateColumns: "1fr 360px", minWidth: 0 }}>
			{/* ---------------- matrix ---------------- */}
			<div style={{ minWidth: 0 }}>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "200px 150px 1fr 150px",
						gap: 12,
						padding: "12px 24px",
						...eyebrowSx,
						color: lbb.ink60,
					}}
				>
					<div>Work center</div>
					<div>Role required</div>
					<div style={{ display: "flex", gap: 8 }}>
						{blocks.map((block) => (
							<div
								key={block.label}
								style={{
									flex: 1,
									paddingLeft: 14,
									fontFamily: lbb.fontMono,
									letterSpacing: "0.06em",
								}}
							>
								{block.label}
							</div>
						))}
					</div>
					<div style={{ textAlign: "right" }}>Gap</div>
				</div>

				{rows.map((row, index) => {
					const firstOfCenter = index === 0 || rows[index - 1].workcenter !== row.workcenter;
					return (
						<div
							key={`${row.workcenter}-${row.jobRole}`}
							style={{
								display: "grid",
								gridTemplateColumns: "200px 150px 1fr 150px",
								gap: 12,
								alignItems: "center",
								padding: "8px 24px",
								borderTop: firstOfCenter ? lbb.borderInk : lbb.borderHair,
							}}
						>
							<div style={{ minWidth: 0 }}>
								{firstOfCenter && (
									<div
										style={{
											fontFamily: lbb.fontUi,
											fontSize: 15,
											fontWeight: 600,
											color: lbb.navy,
											overflowWrap: "anywhere",
										}}
									>
										{row.workcenter}
									</div>
								)}
							</div>
							<div
								style={{
									fontFamily: lbb.fontUi,
									fontSize: 14,
									color: lbb.navy,
									overflowWrap: "anywhere",
								}}
							>
								{row.jobRole}
							</div>

							<div style={{ display: "flex", gap: 8, minWidth: 0 }}>
								{row.cells.map((cell) => {
									const key = gapKey(row.workcenter, row.jobRole, cell.blockIndex);
									const drafted = Boolean(drafts[key]);
									const isSelected = selected === key;
									const isShort = cell.gapHours > 0 && !drafted;

									// Covered is a STATE, not an absence — a white cell reads as
									// missing data. It gets the same calm green as a drafted cover;
									// the two are told apart by the tick and the ink border, not by
									// hue, because both mean "this block is fine".
									let style: React.CSSProperties = {
										...CELL_BASE,
										background: lbb.successTint,
										color: lbb.success,
									};
									let label = "·";

									if (drafted) {
										style = {
											...CELL_BASE,
											background: lbb.successTint,
											color: lbb.success,
											border: lbb.borderInk,
										};
										label = "✓";
									} else if (isSelected) {
										style = {
											...CELL_BASE,
											background: lbb.brick,
											color: lbb.paper,
											border: lbb.borderInk,
										};
										label = `−${cell.headsShort}`;
									} else if (isShort) {
										style = {
											...CELL_BASE,
											background: lbb.brickTint,
											color: lbb.brick,
											cursor: "pointer",
										};
										label = `−${cell.headsShort}`;
									}

									return isShort || isSelected ? (
										<button
											key={cell.blockIndex}
											type="button"
											onClick={() => onSelect(isSelected ? null : key)}
											title={`${cell.reqHours.toFixed(1)}h required · ${cell.haveHours.toFixed(1)}h covered`}
											style={style}
										>
											{label}
										</button>
									) : (
										<div
											key={cell.blockIndex}
											title={
												drafted
													? "Cover drafted"
													: `${cell.reqHours.toFixed(1)}h required · ${cell.haveHours.toFixed(1)}h covered`
											}
											style={style}
										>
											{label}
										</div>
									);
								})}
							</div>

							<div
								style={{
									textAlign: "right",
									fontFamily: lbb.fontMono,
									fontWeight: 700,
									fontSize: 12,
									color: row.shortHours > 0 ? lbb.brick : lbb.success,
								}}
							>
								{row.shortHours > 0
									? `${Math.round(row.shortHours)} worker-hour${Math.round(row.shortHours) === 1 ? "" : "s"} short`
									: "covered"}
							</div>
						</div>
					);
				})}

				{rows.length === 0 && (
					<div
						style={{
							padding: 48,
							textAlign: "center",
							fontFamily: lbb.fontUi,
							color: lbb.ink60,
						}}
					>
						No job orders with a labor standard on this day — nothing to measure demand against.
					</div>
				)}

				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						gap: 24,
						padding: "16px 24px",
						borderTop: lbb.borderInk,
						background: lbb.alloy,
						fontFamily: lbb.fontUi,
						fontSize: 13,
						color: lbb.ink60,
						flexWrap: "wrap",
					}}
				>
					<div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
						<Legend color={lbb.brickTint} border={lbb.brick} label="short — click to cover" />
						<Legend color={lbb.successTint} border={lbb.ink} label="cover drafted" />
						<Legend color={lbb.successTint} border={lbb.hairline} label="covered" />
					</div>
					<span style={{ fontVariantNumeric: "tabular-nums" }}>
						{shortBlocks} short 4h block{shortBlocks === 1 ? "" : "s"} ·{" "}
						{Math.round(uncoveredHours)} worker-hour
						{Math.round(uncoveredHours) === 1 ? "" : "s"} uncovered on {SHIFT_LABEL[shift]}
					</span>
				</div>
			</div>

			{/* ---------------- cover rail ---------------- */}
			<div
				style={{
					background: lbb.alloyWarm,
					borderLeft: lbb.borderInk,
					padding: 20,
					minWidth: 0,
				}}
			>
				{!selectedCell || !selectedRow ? (
					<>
						<div style={{ ...eyebrowSx, color: lbb.ink60 }}>Cover a gap</div>
						<div
							style={{
								fontFamily: lbb.fontBrand,
								fontSize: 22,
								fontWeight: 700,
								color: lbb.navy,
								lineHeight: 1.2,
								marginTop: 8,
							}}
						>
							Click any red block to see who can cover it.
						</div>
						<p
							style={{
								fontFamily: lbb.fontUi,
								fontSize: 14,
								color: lbb.ink60,
								lineHeight: 1.5,
								marginTop: 12,
							}}
						>
							Three ways to close a gap: offer the block as overtime to crew already on shift,
							request flex workers from the pool, or pull a qualified worker who has slack on
							another line.
						</p>
					</>
				) : (
					<CoverRail
						row={selectedRow}
						cell={selectedCell}
						shift={shift}
						drafted={drafts[selected as string]}
						pull={pullSource(selectedRow, selectedCell)}
						onDraft={(method) => onDraft(selected as string, method)}
					/>
				)}
			</div>
		</div>
	);
}

const Legend: React.FC<{ color: string; border: string; label: string }> = ({
	color,
	border,
	label,
}) => (
	<span style={{ display: "flex", alignItems: "center", gap: 8 }}>
		<span style={{ width: 12, height: 12, background: color, border: `1px solid ${border}` }} />
		{label}
	</span>
);

const CoverRail: React.FC<{
	row: GapRow
	cell: GapCell
	shift: ShiftNumber
	drafted?: string
	pull: { workcenter: string; detail: string } | null
	onDraft: (method: CoverMethod) => void
}> = ({ row, cell, shift, drafted, pull, onDraft }) => (
	<>
		<div style={{ ...eyebrowSx, color: lbb.brick }}>
			{drafted ? "Request drafted" : "Pick a cover"}
		</div>
		<div
			style={{
				fontFamily: lbb.fontBrand,
				fontSize: 24,
				fontWeight: 700,
				letterSpacing: "-0.025em",
				color: lbb.navy,
				lineHeight: 1.15,
				marginTop: 8,
				overflowWrap: "anywhere",
			}}
		>
			{row.workcenter} · {row.jobRole}
		</div>
		<div
			style={{
				fontFamily: lbb.fontMono,
				fontSize: 12,
				color: lbb.ink60,
				marginTop: 6,
			}}
		>
			{cell.block.label} · {SHIFT_LABEL[shift]} · 4h block
		</div>
		<div
			style={{
				fontFamily: lbb.fontUi,
				fontSize: 14,
				fontWeight: 600,
				color: lbb.brick,
				marginTop: 12,
			}}
		>
			{cell.headsShort} short · {row.qualifiedHeads} qualified head
			{row.qualifiedHeads === 1 ? "" : "s"} · {Math.round(cell.gapHours)} worker-hour
			{Math.round(cell.gapHours) === 1 ? "" : "s"} to fill
		</div>

		<div style={{ borderTop: lbb.borderInk, margin: "16px 0" }} />

		{drafted ? (
			<div
				style={{
					fontFamily: lbb.fontUi,
					fontSize: 14,
					color: lbb.ink60,
					lineHeight: 1.5,
				}}
			>
				Drafted as <strong style={{ color: lbb.navy }}>{drafted}</strong>. It stays local until you
				publish — nothing has been sent to anyone yet.
			</div>
		) : (
			<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
				<CoverOption
					label="Offer a 4h OT block to on-shift crew"
					detail={`${row.qualifiedHeads} qualified on the roster · 1.5× premium · ~$${Math.round(cell.gapHours) * 62} for the block`}
					onClick={() => onDraft("ot")}
				/>
				<CoverOption
					label={`Request ${cell.headsShort} flex worker${cell.headsShort === 1 ? "" : "s"} · 4h block`}
					detail="Offered to the flex pool · median fill 42 min"
					onClick={() => onDraft("flex")}
				/>
				{pull && (
					<CoverOption
						label={`Pull from ${pull.workcenter}`}
						detail={pull.detail}
						onClick={() => onDraft("pull")}
					/>
				)}
			</div>
		)}
	</>
);

const CoverOption: React.FC<{ label: string; detail: string; onClick: () => void }> = ({
	label,
	detail,
	onClick,
}) => (
	<button
		type="button"
		onClick={onClick}
		style={{
			display: "block",
			width: "100%",
			textAlign: "left",
			padding: 14,
			background: lbb.paper,
			border: lbb.borderInk,
			borderRadius: 4,
			cursor: "pointer",
			font: "inherit",
		}}
	>
		<div style={{ fontFamily: lbb.fontUi, fontSize: 15, fontWeight: 700, color: lbb.navy }}>
			{label}
		</div>
		<div style={{ fontFamily: lbb.fontUi, fontSize: 13, color: lbb.ink60, marginTop: 2 }}>
			{detail}
		</div>
	</button>
);

export default GapBoard;
